import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DatePicker,
  DropdownField,
  TimePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  CollapsibleSection,
} from '@roger/ui-components';
import {
  createInfectionLog,
  getInfectionLogDetail,
  updateInfectionLog,
  deleteInfectionLog,
} from '../../services/api';
import type {
  AvailableClinician,
  ClinicPhysicianResponse,
  InfectionLogDetailResponse,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { useEpisodeDateBounds } from '../../hooks/useEpisodeDateBounds';
import { useLogDelete } from '../../hooks/useLogDelete';
import { parse4xxHeyApiResult } from '../../utils/apiErrorDetail';
import { getClinicianDisplayName } from '../../utils/clinicianUtils';
import { formatPhysicianName } from '../../utils/formatting';
import { InfectionSourceNotice } from './InfectionSourceNotice';
import InfectionInformationSection from './InfectionInformationSection';
import InfectionActionsSection from './InfectionActionsSection';
import InfectionFollowUpSection from './InfectionFollowUpSection';
import {
  buildInfectionLogCreateBody,
  buildInfectionLogUpdateBody,
  detailToInfectionLogForm,
  emptyInfectionLogForm,
  type InfectionLogFormState,
  type UpdateInfectionLogField,
} from './infectionLogForm';
import { InfectionFieldLabel } from './infectionLogFormFields';

interface AddInfectionLogDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  episodeId?: string | null;
  /** The log's episode's clinical manager; shown read-only (EMR-2700). */
  episodeClinicalManagerName?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  clinicians: AvailableClinician[];
  physicians: ClinicPhysicianResponse[];
  onCreated: () => void;
  /** If set, load this log and edit it instead of creating new */
  editingLogId?: string | null;
  /** Already-fetched log data — skips the GET request if provided (avoids double-fetch from detail dialog) */
  editingLogData?: InfectionLogDetailResponse | null;
}

export const AddInfectionLogDialog: React.FC<AddInfectionLogDialogProps> = ({
  open,
  onClose,
  patientId,
  episodeId,
  episodeClinicalManagerName,
  episodeStartDate,
  episodeEndDate,
  clinicians,
  physicians,
  onCreated,
  editingLogId,
  editingLogData,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<InfectionLogFormState>(
    emptyInfectionLogForm
  );
  const isEditing = Boolean(editingLogId);

  const resetForm = () => setForm(emptyInfectionLogForm());

  const { deleting, confirmDeleteOpen, setConfirmDeleteOpen, handleDelete } =
    useLogDelete({
      entityLabel: 'infection log',
      performDelete: () =>
        deleteInfectionLog({
          path: { patient_id: patientId, infection_log_id: editingLogId! },
        }),
      onDeleted: () => {
        resetForm();
        onCreated();
      },
    });

  // Date Reported must be within the episode and not in the future
  const {
    minDate: minReportedDate,
    maxDate: maxReportedDate,
    today,
  } = useEpisodeDateBounds(open, episodeStartDate, episodeEndDate);

  // Same searchable picker as the order, communication and add-task
  // physician fields: a clinic roster can run to thousands of names.
  const physicianOptions = useMemo(
    () =>
      physicians.map(p => ({
        value: p.id,
        label: formatPhysicianName(p),
      })),
    [physicians]
  );

  const updateField: UpdateInfectionLogField = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]:
        typeof value === 'function'
          ? (
              value as (
                prevValue: InfectionLogFormState[typeof key]
              ) => InfectionLogFormState[typeof key]
            )(prev[key])
          : value,
    }));
  };

  const isFormValid = !!form.dateReported && !!form.infectionType;

  // Pre-fill form when editing an existing log
  useEffect(() => {
    if (!open || !editingLogId) return;
    (async () => {
      try {
        // Use cached data if provided (avoids double-fetch from detail dialog)
        let d = editingLogData ?? null;
        if (!d) {
          const res = await getInfectionLogDetail({
            path: { patient_id: patientId, infection_log_id: editingLogId },
          });
          d = res.data ?? null;
        }
        if (!d) return;
        setForm(detailToInfectionLogForm(d));
      } catch (err) {
        console.error('Failed to load infection log:', err);
      }
    })();
  }, [open, editingLogId, editingLogData]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.dateReported) {
      showError('Validation', 'Date Reported is required.');
      return;
    }
    if (!form.infectionType) {
      showError('Validation', 'Infection Type is required.');
      return;
    }

    setSubmitting(true);
    try {
      let res;
      if (isEditing && editingLogId) {
        res = await updateInfectionLog({
          path: {
            patient_id: patientId,
            infection_log_id: editingLogId,
          },
          body: buildInfectionLogUpdateBody(form),
        });
      } else {
        res = await createInfectionLog({
          path: { patient_id: patientId },
          body: buildInfectionLogCreateBody(form, episodeId),
        });
      }

      if (res.error) {
        console.error('Failed to save infection log:', res.error);
        // Surface actionable validation messages (e.g. date outside the
        // episode) — the date pickers can't catch every case, such as
        // timezone differences with the server's business date.
        const { message } = parse4xxHeyApiResult(res);
        showError(
          'Save Failed',
          message ?? 'Failed to save infection log. Please try again.'
        );
        return;
      }

      showSuccess('Infection Log Saved', 'Infection log saved.');
      resetForm();
      onCreated();
    } catch (err) {
      console.error('Error creating infection log:', err);
      showError('Error', 'Failed to save infection log. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Infection Log' : 'Log New Infection'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Add a new infection log for this patient
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          {form.sourceEvidence && (
            <InfectionSourceNotice source={form.sourceEvidence} />
          )}
          {/* ── Patient Information ─────────────────────────────── */}
          <div className='border rounded-lg px-4 py-3 space-y-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Patient Information
            </p>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              {/* Row 1: Primary Physician | Case Manager */}
              <div>
                <DropdownField
                  label='Primary Physician'
                  htmlFor='infection-log-primary-physician'
                  sortOptions
                  placeholder='Select physician'
                  searchPlaceholder='Search physicians...'
                  enableFiltering
                  isClearable={false}
                  popoverWidth='min(28rem, 90vw)'
                  options={physicianOptions}
                  value={form.primaryPhysicianId}
                  onChange={v => updateField('primaryPhysicianId', v)}
                />
              </div>

              <div>
                <InfectionFieldLabel>Case Manager</InfectionFieldLabel>
                <Select
                  value={form.caseManagerId}
                  onValueChange={v => updateField('caseManagerId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  data-testid='infection-log-clinical-manager'
                  className='mt-1 text-xs text-muted-foreground'
                >
                  Clinical Manager: {episodeClinicalManagerName || 'Unassigned'}
                </p>
              </div>

              {/* Row 2: Infection Reported By | Infection Reported To */}
              <div>
                <InfectionFieldLabel>Infection Reported By</InfectionFieldLabel>
                <Select
                  value={form.reportedByUserId}
                  onValueChange={v => updateField('reportedByUserId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <InfectionFieldLabel>Infection Reported To</InfectionFieldLabel>
                <Select
                  value={form.reportedToUserId}
                  onValueChange={v => updateField('reportedToUserId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={`to-${c.user_id}`} value={c.user_id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Row 3: Date Reported | Time Reported */}
              <div>
                <InfectionFieldLabel required>
                  Date Reported
                </InfectionFieldLabel>
                <DatePicker
                  date={form.dateReported}
                  onDateChange={d => updateField('dateReported', d)}
                  minDate={minReportedDate}
                  maxDate={maxReportedDate}
                />
              </div>

              <div>
                <InfectionFieldLabel>Time Reported</InfectionFieldLabel>
                <TimePicker
                  time={form.timeReported || undefined}
                  onTimeChange={time => updateField('timeReported', time ?? '')}
                />
              </div>
            </div>
          </div>

          <InfectionInformationSection
            form={form}
            updateField={updateField}
            isEditing={isEditing}
            today={today}
            sourceEvidence={form.sourceEvidence}
          />

          <InfectionActionsSection
            form={form}
            updateField={updateField}
            physicianOptions={physicianOptions}
          />

          {/* ── Medication Administration ────────────────────────── */}
          <CollapsibleSection
            title='Medication Administration'
            defaultOpen={false}
          >
            <p className='text-sm text-muted-foreground'>
              Medication administration for this infection is managed on the{' '}
              <button
                type='button'
                onClick={handleClose}
                className='font-medium text-primary underline underline-offset-2 hover:text-primary/80'
              >
                Medications
              </button>{' '}
              tab.
            </p>
          </CollapsibleSection>

          <InfectionFollowUpSection
            form={form}
            updateField={updateField}
            clinicians={clinicians}
          />
        </div>

        <DialogFooter className='gap-2 mt-2'>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={submitting || deleting}
          >
            Cancel
          </Button>
          {isEditing && (
            <Button
              variant='destructive'
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={submitting || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting || deleting || !isFormValid}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
        <ConfirmationDialog
          open={confirmDeleteOpen}
          title='Delete Infection Log'
          message={
            form.sourceEvidence
              ? 'Remove this infection log from the chart? The original imported source evidence remains retained.'
              : 'This will permanently delete this infection log and its follow-up history. This action cannot be undone.'
          }
          confirmLabel='Delete'
          loading={deleting}
          onAccept={handleDelete}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
